import clsx from 'clsx'
import { PackageSearch, Search, Sparkles, Flame } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import type { AppConfigResponse, BannersConfig, CatalogFilter, CollectionsConfig, Product, ProductsResponse } from '@/types'
import { ProductCard } from '@/components/ProductCard'
import { SocialModal } from '@/components/SocialModal'
import { EmptyState, ErrorNote, LoadingScreen } from '@/components/ui'
import { api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'

const TABS: { key: CatalogFilter; label: string; icon?: React.ComponentType<{ className?: string }> }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'hot', label: 'Hot Items', icon: Flame },
  { key: 'new', label: 'Novedades', icon: Sparkles },
]

export function Catalog() {
  const [tab, setTab] = useState<CatalogFilter>('all')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [shareTarget, setShareTarget] = useState<Product | null>(null)
  const [collections, setCollections] = useState<CollectionsConfig | null>(null)
  const [banners, setBanners] = useState<BannersConfig>({})

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    api.get<AppConfigResponse>('/config').then((r) => setCollections(r.collections)).catch(() => {})
    api.get<BannersConfig>('/banners').then(setBanners).catch(() => {})
  }, [])

  const path = useMemo(() => {
    const qs = new URLSearchParams({ filter: tab })
    if (debounced) qs.set('search', debounced)

    // Hot/Novedades: resolver la colección configurada + vigencia (RN-02).
    if (tab !== 'all') {
      const c = tab === 'hot' ? collections?.hot : collections?.new
      const today = new Date().toISOString().slice(0, 10)
      const inWindow = !!c && (!c.startDate || today >= c.startDate) && (!c.endDate || today <= c.endDate)
      if (c && inWindow) qs.set('collection', c.collectionId)
    }
    return `/products?${qs.toString()}`
  }, [tab, debounced, collections])

  const { data, loading, error, reload } = useAsync(
    () => api.get<ProductsResponse>(path),
    [path]
  )

  const products = data?.products ?? []

  return (
    <div className="flex flex-col">
      {/* Buscador */}
      <div className="sticky top-0 z-10 space-y-3 bg-surface px-4 pb-3 pt-4">
        <div className="flex items-center gap-2 rounded-xl border border-line bg-surface-sunken px-3">
          <Search className="h-4 w-4 text-ink-faint" />
          <input
            className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-ink-faint"
            placeholder="Buscar producto…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="no-scrollbar flex gap-2 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={clsx('chip', tab === key && 'chip-active')}
              onClick={() => setTab(key)}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenido */}
      {loading ? (
        <LoadingScreen label="Cargando catálogo…" />
      ) : error ? (
        <ErrorNote message={error.message} onRetry={reload} />
      ) : products.length === 0 ? (
        <EmptyState
          icon={PackageSearch}
          title={
            debounced
              ? 'Sin resultados'
              : tab === 'all'
                ? 'No hay productos disponibles'
                : 'Sección sin configurar'
          }
          subtitle={
            debounced
              ? `No encontramos productos para "${debounced}".`
              : tab === 'all'
                ? 'Probá con otra búsqueda.'
                : 'El admin todavía no configuró una colección para esta sección.'
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-3 p-4">
          {products.map((p) => (
            <ProductCard key={`${p.productId}-${p.skuId}`} product={p} onShare={setShareTarget} />
          ))}
        </div>
      )}

      <SocialModal product={shareTarget} onClose={() => setShareTarget(null)} banners={banners} />
    </div>
  )
}
